import { createSelector } from "@reduxjs/toolkit";
import {
  selectActiveRoomColoring,
  selectActiveProjectTab,
  selectRoomNumbers,
} from "../Room/roomSelectors";
import { selectHttpRoomsBasedOnActiveProjectTab } from "../Room/httpRoomSelectors";
import { selectHttpProject } from "../projectSelectors";
import { selectProjectNo } from "../DesignTab/designSlice";
import { PostMultiplacerRoomSnapshotTrendByKeyApiArg } from "../../openapi";
import { buildBucketConfigFromColorSpecs } from "../roomColoringUtils";
import { selectSelectedPnsPrefixes } from "../DesignTab/routing/pnsSelectors";

type ISpaceColorMapping = {
  [spaceName: string]: string; // spaceName -> colorHex
};

export const selectSpaceColorHashmapFromRoomColoring = createSelector(
  [selectHttpRoomsBasedOnActiveProjectTab, selectActiveRoomColoring],
  (rooms, activeRoomColoring) => {
    const colorMapping: ISpaceColorMapping = {};

    if (!activeRoomColoring) {
      return colorMapping;
    }

    return Object.entries(rooms).reduce((acc, [spaceName, room]) => {
      const colorHex = activeRoomColoring.getPolygonColors({
        room: room,
        colorSpecs: activeRoomColoring.colorSpecs,
      }).fill;

      acc[spaceName] = colorHex ?? "#CCCCCC";
      return acc;
    }, colorMapping);
  }
);

export interface IRoomColoringChartDatum {
  label: string;
  value: number;
  color: string;
}

export interface IRoomColoringChartData {
  chartData: IRoomColoringChartDatum[];
  totalCount: number;
}

export const selectRoomColoringChartData = createSelector(
  [selectSpaceColorHashmapFromRoomColoring, selectActiveRoomColoring],
  (spaceColorMap, activeRoomColoring): IRoomColoringChartData => {
    if (!activeRoomColoring) {
      return { chartData: [], totalCount: 0 };
    }

    const countsByColor: Record<string, number> = {};

    // räkna hur många spaces per färg
    Object.values(spaceColorMap).forEach((hex) => {
      if (!hex) return;
      countsByColor[hex] = (countsByColor[hex] ?? 0) + 1;
    });

    const chartData: IRoomColoringChartDatum[] = [];
    let totalCount = 0;

    // gå igenom definierade färger i ordning från colorSpecs
    for (const spec of activeRoomColoring.colorSpecs) {
      const count = countsByColor[spec.fillColor] ?? 0;
      if (count <= 0) continue;

      chartData.push({
        label: spec.explanation,
        value: count,
        color: spec.fillColor,
      });
      totalCount += count;
    }

    // ev. “övriga” färger om något ligger utanför specs
    const knownColors = new Set<string>(
      activeRoomColoring.colorSpecs.map((spec) => spec.fillColor)
    );
    const otherCount = Object.values(spaceColorMap).reduce<number>((acc, hex) => {
      if (!hex) return acc;
      if (knownColors.has(hex)) return acc;
      return acc + 1;
    }, 0);

    if (otherCount > 0) {
      chartData.push({
        label: "Andre / uten definert farge",
        value: otherCount,
        color: "#cccccc",
      });
      totalCount += otherCount;
    }

    return { chartData, totalCount };
  }
);

export const selectTrendKeyForRoomColoring = createSelector(
  [selectHttpProject, selectActiveRoomColoring],
  (project, roomColoring): string | null => {
    if (!project || !roomColoring || !roomColoring.trendKeyPath) {
      return null;
    }
    return roomColoring.trendKeyPath;
  }
);

// ✅ build the RTK Query args in a selector
export const selectTrendByKeyArgsForRoomColoring = createSelector(
  [
    selectProjectNo,
    selectActiveRoomColoring,
    selectRoomNumbers,
    selectSelectedPnsPrefixes,
    selectActiveProjectTab,
  ],
  (
    projectNo,
    activeRoomColoring,
    roomNumbers,
    selectedPnsPrefixes,
    activeProjectTab
  ): PostMultiplacerRoomSnapshotTrendByKeyApiArg | null => {
    const trendKey = activeRoomColoring?.trendKeyPath;
    if (!projectNo || !trendKey) return null;
    if (activeProjectTab !== "room" && roomNumbers.length === 0) return null;

    const dateTo = new Date();
    const dateFrom = new Date(dateTo);
    dateFrom.setMonth(dateFrom.getMonth() - 6); // last ~6 months

    // ✅ Transform ranges from colorSpecs -> bucketConfig (only when numeric)
    const bucketConfig = buildBucketConfigFromColorSpecs(activeRoomColoring?.colorSpecs);

    return {
      body: {
        projectNo,
        keyPath: trendKey,
        granularity: "week",
        excludeEmptyString: true,
        filter: {
          ...(activeProjectTab === "room"
            ? { pns: selectedPnsPrefixes }
            : { roomNumbers }),
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
        },

        ...(bucketConfig ? { bucketConfig } : {}),
      },
    };
  }
);
